import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { agentConfigService, ExampleConversation, ExampleConversationCreate, ExampleConversationUpdate } from '../../services/agentConfigService';

const TONE_CATEGORIES = [
  { value: 'professional', label: 'Profesional' },
  { value: 'friendly', label: 'Amigable' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
  { value: 'helpful', label: 'Servicial' },
  { value: 'sales_oriented', label: 'Orientado a Ventas' },
  { value: 'informative', label: 'Informativo' },
  { value: 'empathetic', label: 'Empático' }
];

const ExampleConversationManager: React.FC = () => {
  const [conversations, setConversations] = useState<ExampleConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConversation, setEditingConversation] = useState<ExampleConversation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTone, setFilterTone] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Form state
  const [formData, setFormData] = useState({
    conversation_name: '',
    user_message: '',
    expected_response: '',
    tone_category: 'professional'
  });

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      handleSearch();
    } else {
      loadConversations();
    }
  }, [searchTerm]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading example conversations...');
      const params = filterTone ? { tone_category: filterTone } : undefined;
      console.log('📋 Request params:', params);
      const data = await agentConfigService.getExampleConversations(params);
      console.log('✅ Example conversations loaded:', data);
      setConversations(data);
    } catch (error) {
      console.error('❌ Error loading example conversations:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: (error as any)?.response?.data,
        status: (error as any)?.response?.status,
        statusText: (error as any)?.response?.statusText
      });
      showSnackbar('Error al cargar conversaciones de ejemplo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadConversations();
      return;
    }

    try {
      setLoading(true);
      const data = await agentConfigService.searchExampleConversations(searchTerm);
      setConversations(data);
    } catch (error) {
      showSnackbar('Error al buscar conversaciones', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (conversation?: ExampleConversation) => {
    if (conversation) {
      setEditingConversation(conversation);
      setFormData({
        conversation_name: conversation.conversation_name,
        user_message: conversation.user_message,
        expected_response: conversation.expected_response,
        tone_category: conversation.tone_category
      });
    } else {
      setEditingConversation(null);
      setFormData({
        conversation_name: '',
        user_message: '',
        expected_response: '',
        tone_category: 'professional'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConversation(null);
  };

  const handleSave = async () => {
    try {
      if (editingConversation) {
        await agentConfigService.updateExampleConversation(editingConversation.id, formData as ExampleConversationUpdate);
        showSnackbar('Conversación actualizada exitosamente', 'success');
      } else {
        await agentConfigService.createExampleConversation(formData as ExampleConversationCreate);
        showSnackbar('Conversación creada exitosamente', 'success');
      }

      handleCloseDialog();
      loadConversations();
    } catch (error) {
      showSnackbar('Error al guardar conversación', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta conversación de ejemplo?')) {
      try {
        await agentConfigService.deleteExampleConversation(id);
        showSnackbar('Conversación eliminada exitosamente', 'success');
        loadConversations();
      } catch (error) {
        showSnackbar('Error al eliminar conversación', 'error');
      }
    }
  };

  const getToneCategoryLabel = (category: string) => {
    const tone = TONE_CATEGORIES.find(t => t.value === category);
    return tone ? tone.label : category;
  };

  const getToneCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      professional: 'primary',
      friendly: 'success',
      formal: 'info',
      casual: 'warning',
      helpful: 'secondary',
      sales_oriented: 'error',
      informative: 'default',
      empathetic: 'primary'
    };
    return colors[category] || 'default';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatIcon />
          Conversaciones de Ejemplo
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Conversación
        </Button>
      </Box>

      {/* Search and Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Buscar conversaciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtrar por Tono</InputLabel>
            <Select
              value={filterTone}
              label="Filtrar por Tono"
              onChange={(e) => {
                setFilterTone(e.target.value);
                // Reload conversations with new filter
                setTimeout(() => loadConversations(), 100);
              }}
            >
              <MenuItem value="">Todos</MenuItem>
              {TONE_CATEGORIES.map((tone) => (
                <MenuItem key={tone.value} value={tone.value}>
                  {tone.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Mensaje del Usuario</TableCell>
              <TableCell>Respuesta Esperada</TableCell>
              <TableCell>Categoría de Tono</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conversations.map((conversation) => (
              <TableRow key={conversation.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {conversation.conversation_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200 }}>
                    {conversation.user_message.length > 100
                      ? `${conversation.user_message.substring(0, 100)}...`
                      : conversation.user_message}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 200 }}>
                    {conversation.expected_response.length > 100
                      ? `${conversation.expected_response.substring(0, 100)}...`
                      : conversation.expected_response}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getToneCategoryLabel(conversation.tone_category)}
                    color={getToneCategoryColor(conversation.tone_category) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(conversation.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(conversation)}
                    title="Editar conversación"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(conversation.id)}
                    title="Eliminar conversación"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Conversation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConversation ? 'Editar Conversación de Ejemplo' : 'Nueva Conversación de Ejemplo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nombre de la Conversación"
              value={formData.conversation_name}
              onChange={(e) => setFormData({ ...formData, conversation_name: e.target.value })}
              fullWidth
              required
            />
            
            <TextField
              label="Mensaje del Usuario"
              value={formData.user_message}
              onChange={(e) => setFormData({ ...formData, user_message: e.target.value })}
              multiline
              rows={3}
              fullWidth
              required
              helperText="El mensaje que enviaría un usuario"
            />

            <TextField
              label="Respuesta Esperada"
              value={formData.expected_response}
              onChange={(e) => setFormData({ ...formData, expected_response: e.target.value })}
              multiline
              rows={4}
              fullWidth
              required
              helperText="La respuesta ideal que debería dar el agente"
            />

            <FormControl fullWidth>
              <InputLabel>Categoría de Tono</InputLabel>
              <Select
                value={formData.tone_category}
                label="Categoría de Tono"
                onChange={(e) => setFormData({ ...formData, tone_category: e.target.value })}
              >
                {TONE_CATEGORIES.map((tone) => (
                  <MenuItem key={tone.value} value={tone.value}>
                    {tone.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingConversation ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExampleConversationManager;